// ----------------------------------------------------------------------------
// -------------------------------------------------------------------- Imports
// ----------------------------------------------------------------------------
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Libraries
import React from "react";
import { css } from "glamor";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Components
import Link from "gatsby-link";
import { Form, Input, Button, Icon } from "antd";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Locals
import { Container, OutLink, Image } from "@bodhi-project/components";
import { Elements } from "@bodhi-project/typography";
import { Footer as SemanticFooter } from "@bodhi-project/semantic-webflow";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Locals
import logoAlpha from "../assets/logoAlpha.png";
import { hasErrors, validateEmail, validateName } from "../helpers/formHelpers";
import { newsletterFormStylesClass } from "../styles/newsletterFormStyles";
import twitterAlpha from "../assets/twitterAlpha.png";
import slackAlpha from "../assets/slackAlpha.png";
import linkedinAlpha from "../assets/linkedinAlpha.png";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Abstractions
const FormItem = Form.Item;
const { Paragraph } = Elements;

// ----------------------------------------------------------------------------
// --------------------------------------------------------------------- Styles
// ----------------------------------------------------------------------------
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Page style
const footerStyles = css({
  backgroundImage: "linear-gradient(to right, #2A2A2A 0%, #3f3f3f 100%)",
  "& a": {
    color: "#FFFFFF !important",
    borderBottom: "1.625px solid transparent",
    transition: "all 200ms cubic-bezier(0.78, 0.14, 0.15, 0.86)",

    "&:hover": {
      color: "#FFFFFF !important",
      borderBottom: "1.625px solid #FFFFFF !important"
    },
    "&:visited": {
      textDecoration: "none"
    },
    "&:link": {
      textDecoration: "none"
    },
    "&:active": {
      textDecoration: "none"
    }
  },

  "& ul": {
    listStyle: "none",
    textAlign: "center",
    padding: 0,

    "& li": {
      display: "inline-block",
      padding: "0em 1em"
    }
  },
  "& p": {
    color: "#FFFFFF"
  }
});
const footerStylesClass = footerStyles.toString();

// ----------------------------------------------------------------------------
// ------------------------------------------------------------------ Component
// ----------------------------------------------------------------------------
/** Footer */
class Footer extends React.Component {
  /** standard constructor */
  constructor(props) {
    super(props);

    this.state = {
      loader: null,
      formSent: false
    };

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  /** componentDidMount - Disable submit button at the beginning. */
  componentDidMount() {
    this.props.form.validateFields();
  }

  /** handleSubmit - Post to google spreadsheet. */
  handleSubmit(e) {
    e.preventDefault();
    this.props.form.validateFields((err, values) => {
      if (!err) {
        // console.log('Received values of form: ', values);
        this.setState({
          // Show loader and reset errors if any.
          loader: true
        });

        const { name, email, event } = values;

        setTimeout(() => {
          // Mock some delay
          fetch(
            `https://script.google.com/macros/s/AKfycbxe5KaEdHtLH5JVpf-yntF5LZYAszQTwHHQ4tEjvBT4DyykpRtZ/exec?name=${name}&email=${email}&event=${event}&callback=?`,
            {
              method: "GET",
              mode: "no-cors"
            }
          )
            .then(response => {
              console.log("success", response);
              this.setState({
                loader: false,
                formSent: true
              });
            })
            .catch(error => {
              console.log("error", error);
              this.setState({
                loader: false
              });
            });
        }, 1500);
      }
    });
  }

  /** standard renderer */
  render() {
    const {
      getFieldDecorator,
      getFieldsError,
      getFieldError,
      isFieldTouched
    } = this.props.form;
    // Only show error after a field is touched.
    const nameError = isFieldTouched("name") && getFieldError("name");
    const emailError = isFieldTouched("email") && getFieldError("email");

    return (
      <SemanticFooter className={`${footerStylesClass}`}>
        <Container
          style={{ paddingTop: 200, paddingBottom: 100, display: "block" }}
        >
          <Image
            src={logoAlpha}
            alt="DMR Logo"
            style={{
              height: 150,
              width: 139,
              background: "transparent",
              border: "none",
              margin: "0em auto 2em auto"
            }}
          />
          <Paragraph
            className="text-center"
            style={{
              color: "#FFFFFF",
              maxWidth: "40em",
              marginLeft: "auto",
              marginRight: "auto"
            }}
          >
            Secured Adobe DRM Integration Web Services
          </Paragraph>
          <ul className="mask-p">
            <li>
              <Link to="/docs/about-dmr">Why DMR?</Link>
            </li>
            <li>
              <Link to="/about">About Us</Link>
            </li>
            <li>
              <Link to="/contact">Contact Us</Link>
            </li>
          </ul>
          <Paragraph
            className="text-center hide"
            style={{
              color: "#FFFFFF",
              marginBottom: 30
            }}
          >
            Don't miss out on news, features and special offers. Subscribe to
            our newsletter!
          </Paragraph>
          {this.state.formSent === false && (
            <Form
              onSubmit={this.handleSubmit}
              className={`${newsletterFormStylesClass} hide`}
            >
              {/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Name */}
              <FormItem
                validateStatus={nameError ? "error" : ""}
                help={nameError || ""}
              >
                {getFieldDecorator("name", {
                  validateTrigger: ["onChange", "onBlur"],
                  rules: [{ validator: validateName }]
                })(<Input placeholder="Name" />)}
              </FormItem>
              {/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Email */}
              <FormItem
                validateStatus={emailError ? "error" : ""}
                help={emailError || ""}
              >
                {getFieldDecorator("email", {
                  validateTrigger: ["onChange", "onBlur"],
                  rules: [{ validator: validateEmail }]
                })(<Input placeholder="Email" />)}
              </FormItem>
              {/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Submit */}
              <FormItem>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={hasErrors(getFieldsError())}
                  loading={this.state.loader}
                >
                  <i>Keep Me Posted</i>
                </Button>
              </FormItem>
            </Form>
          )}
          {/* On-sent message */}
          {this.state.formSent === true && (
            <Paragraph className="home" style={{ textIndent: 0 }}>
              Thank you for registering! We'll get back to you shortly.
            </Paragraph>
          )}
          <hr
            style={{
              color: "#FFFFFF",
              maxWidth: "60%",
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: 30,
              marginTop: 0
            }}
          />
          <ul className="mask-p">
            <li>
              <OutLink to="https://dmi.systems">DMI Systems</OutLink>
            </li>
            <li>
              <OutLink to="https://nimas.pub">NIMAS Master</OutLink>
            </li>
            <li>
              <OutLink to="https://antrik.com">Antrik</OutLink>
            </li>
            <li>
              <OutLink to="https://edupress.pub">EduPress</OutLink>
            </li>
          </ul>

          <Paragraph
            className="text-center"
            style={{
              color: "#FFFFFF",
              marginBottom: "11vh"
            }}
          >
            <small>
              Photos credit:{" "}
              <OutLink to="https://unsplash.com/@rohittandon">
                rohittandon
              </OutLink>,{" "}
              <OutLink to="https://unsplash.com/@beccatapert">
                beccatapert
              </OutLink>,{" "}
              <OutLink to="https://unsplash.com/@rawpixel">rawpixel</OutLink>,{" "}
              <OutLink to="https://unsplash.com/@satelliteeyes">
                satelliteeyes
              </OutLink>,{" "}
              <OutLink to="https://unsplash.com/@tobiasmrzyk">
                tobiasmrzyk
              </OutLink>, <br />
              <OutLink to="https://unsplash.com/@levijacobprice">
                levijacobprice
              </OutLink>,{" "}
              <OutLink to="https://unsplash.com/@jareeign">jareeign</OutLink>,{" "}
              <OutLink to="https://unsplash.com/@nasa">nasa</OutLink> at{" "}
              <OutLink to="https://unsplash.com/">Unsplash</OutLink>
            </small>
          </Paragraph>

          <Paragraph
            className="text-center"
            style={{
              color: "#FFFFFF"
            }}
          >
            <span role="img" aria-label="Copyright">
              ©
            </span>{" "}
            Digital Media Initiatives 2007 — 2018
          </Paragraph>
          <Paragraph
            className="text-center"
            style={{
              color: "#FFFFFF"
            }}
          >
            Made with{" "}
            <Icon type="heart" style={{ color: "#d54177", fontSize: "80%" }} />{" "}
            by{" "}
            <OutLink to="https://www.bodhiproject.org/">Bodhi Project</OutLink>.
          </Paragraph>
        </Container>
      </SemanticFooter>
    );
  }
}

Footer.propTypes = {};

const WrappedComponent = Form.create()(Footer);

// --------------------------------------------------------------------- Export
export default WrappedComponent;



// WEBPACK FOOTER //
// ./src/components/Footer.jsx